/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, Group, Input, NumberInput, Paper, SegmentedControl, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCurrencyReal, IconInfoCircle, IconTax } from '@tabler/icons-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { ProposalTipoPrecificacaoEnum } from '../models/core/proposals.type';
import theme from '../theme';
import { Financial } from '../utils/constants.utils';
import { formatCurrency } from '../utils/formatter.utils';
import { isNullOrEmpty } from '../utils/helper.utils';
import { ComboBoxDataV5 } from '../utils/mantine.utils';
import BasicValueUnits, { BasicValueUnitsRef } from './BasicValueUnits';
import PageViewProperty from './core/PageViewProperty/PageViewProperty';

export type BasicPricingData = {
  tipoPrecificacao: ProposalTipoPrecificacaoEnum;
  quantidade: number | null;
  quantidadeIdUnidadeMedida: string | null;
  frequenciaIdUnidadeMedida: string | null;
  preco: number | null;
  imposto: number | null;
};

export type BasicPricingRef = {
  validate(): BasicPricingData | null;
  clear(): void;
};

type BasicPricingProps = {
  isEdit: boolean;
  disabled?: boolean;
  hideTipoPrecificacaoSelector?: boolean;
  forcePricingActive?: boolean;
  withFrequency?: boolean;
  validationMode?: 'all' | 'all-or-none';
  quantityMin?: number;
  quantityDefaultValue?: number;
  quantityDecimalScale?: number;
  quantityStep?: number;
  quantityUnitDisabled?: boolean;
  value: BasicPricingData;
  quantityUnitOptions: ComboBoxDataV5[];
  zIndex: number;
  frequencyUnitOptions?: ComboBoxDataV5[];
  frequencyUnitDisabled?: boolean;
  onTipoPrecificacaoChange?: (tipoPrecificacao: ProposalTipoPrecificacaoEnum) => void;
  onChange?: (value: BasicPricingData) => void;
};

function formatNumber(value: number | null): string {
  if (isNullOrEmpty(value)) {
    return '-';
  }

  return Number(value).toFixed(2);
}

function isPricingActive(
  tipoPrecificacao: ProposalTipoPrecificacaoEnum,
  forcePricingActive?: boolean
): boolean {
  return !!forcePricingActive || tipoPrecificacao === ProposalTipoPrecificacaoEnum.Geral;
}

function normalizePricingValue(value: BasicPricingData): BasicPricingData {
  return {
    ...value,
    imposto: value.imposto ?? Financial.Tax,
  };
}

function serializePricingValue(value: BasicPricingData): string {
  return JSON.stringify(value);
}

const BasicPricing = forwardRef((props: BasicPricingProps, ref) => {
  const {
    frequenciaIdUnidadeMedida,
    imposto,
    preco,
    quantidade,
    quantidadeIdUnidadeMedida,
    tipoPrecificacao,
  } = props.value;
  const refValueUnits = useRef<BasicValueUnitsRef>(null);
  const lastEmittedValueRef = useRef<string | null>(null);
  const onChangeRef = useRef(props.onChange);
  const onTipoPrecificacaoChangeRef = useRef(props.onTipoPrecificacaoChange);

  useEffect(() => {
    onChangeRef.current = props.onChange;
  }, [props.onChange]);

  useEffect(() => {
    onTipoPrecificacaoChangeRef.current = props.onTipoPrecificacaoChange;
  }, [props.onTipoPrecificacaoChange]);

  const form = useForm<BasicPricingData>({
    initialValues: {
      ...normalizePricingValue(props.value),
    },
    validate: {
      preco: (value, values) => {
        if (!isPricingActive(values.tipoPrecificacao, props.forcePricingActive)) {
          return null;
        }

        return isNullOrEmpty(value) || Number.isNaN(value) ? 'Campo obrigatorio' : null;
      },
      imposto: (value, values) => {
        if (!isPricingActive(values.tipoPrecificacao, props.forcePricingActive)) {
          return null;
        }

        return isNullOrEmpty(value) || Number.isNaN(value) ? 'Campo obrigatorio' : null;
      },
    },
  });

  const emitChange = useCallback(
    (valueUnitsData?: { valor: number | null; idUnidade1: string | null; idUnidade2: string | null }) => {
      const nextValueUnitsData = valueUnitsData || {
        valor: form.values.quantidade,
        idUnidade1: form.values.quantidadeIdUnidadeMedida,
        idUnidade2: form.values.frequenciaIdUnidadeMedida,
      };

      const nextValue = {
        tipoPrecificacao: form.values.tipoPrecificacao,
        quantidade: isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
          ? nextValueUnitsData.valor
          : null,
        quantidadeIdUnidadeMedida: nextValueUnitsData.idUnidade1,
        frequenciaIdUnidadeMedida: props.withFrequency ? nextValueUnitsData.idUnidade2 : null,
        preco: isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
          ? form.values.preco
          : null,
        imposto: isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
          ? form.values.imposto
          : null,
      };

      lastEmittedValueRef.current = serializePricingValue(nextValue);
      onChangeRef.current?.(nextValue);
    },
    [
      form.values.frequenciaIdUnidadeMedida,
      form.values.imposto,
      form.values.preco,
      form.values.quantidade,
      form.values.quantidadeIdUnidadeMedida,
      form.values.tipoPrecificacao,
      props.withFrequency,
    ]
  );

  useEffect(() => {
    const nextExternalValue = normalizePricingValue({
      tipoPrecificacao,
      quantidade,
      quantidadeIdUnidadeMedida,
      frequenciaIdUnidadeMedida,
      preco,
      imposto,
    });
    const nextExternalValueKey = serializePricingValue(nextExternalValue);
    const currentFormValueKey = serializePricingValue(form.values);

    if (nextExternalValueKey === currentFormValueKey) {
      return;
    }

    if (nextExternalValueKey === lastEmittedValueRef.current) {
      lastEmittedValueRef.current = null;
      return;
    }

    form.setValues(nextExternalValue);
  }, [frequenciaIdUnidadeMedida, imposto, preco, quantidade, quantidadeIdUnidadeMedida, tipoPrecificacao]);

  useEffect(() => {
    emitChange();
  }, [
    form.values.frequenciaIdUnidadeMedida,
    form.values.imposto,
    form.values.preco,
    form.values.quantidade,
    form.values.quantidadeIdUnidadeMedida,
    form.values.tipoPrecificacao,
  ]);

  useImperativeHandle(ref, () => ({
    validate() {
      if (!isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)) {
        return {
          tipoPrecificacao: form.values.tipoPrecificacao,
          quantidade: null,
          quantidadeIdUnidadeMedida: null, // form.values.quantidadeIdUnidadeMedida,
          frequenciaIdUnidadeMedida: null, // props.withFrequency ? form.values.frequenciaIdUnidadeMedida : null,
          preco: null,
          imposto: null,
        };
      }

      const valueUnitsData = refValueUnits.current?.validate();

      if (form.validate().hasErrors || !valueUnitsData) {
        return null;
      }

      return {
        tipoPrecificacao: form.values.tipoPrecificacao,
        quantidade: valueUnitsData.valor,
        quantidadeIdUnidadeMedida: valueUnitsData.idUnidade1,
        frequenciaIdUnidadeMedida: props.withFrequency ? valueUnitsData.idUnidade2 : null,
        preco: form.values.preco,
        imposto: form.values.imposto,
      };
    },
    clear() {
      form.reset();
      refValueUnits.current?.clear();
    },
  }));

  return (
    <Paper withBorder p="md" radius="md">
      <Grid
        columns={(props.withFrequency ? 8 : 7) - (props.hideTipoPrecificacaoSelector ? 2 : 0)}
        align="end"
      >
        {props.isEdit && !props.hideTipoPrecificacaoSelector && (
          <Grid.Col span={2}>
            <div>
              <Input.Wrapper label="Precificação" required>
                <Group
                  style={{
                    display: 'inline-block',
                    marginLeft: 5,
                    marginTop: -2,
                    verticalAlign: 'bottom',
                  }}
                >
                  <Tooltip
                    label={
                      <div style={{ whiteSpace: 'pre-line' }}>
                        <strong>Geral:</strong> Valor único a ser cobrado do cliente para esse item.
                        {'\n'}
                        <strong>Específica:</strong> Valor a ser cobrado do cliente será definido nas
                        cotações.
                      </div>
                    }
                    multiline
                    withArrow
                    radius="md"
                    fz="sm"
                    p="sm"
                    zIndex={props.zIndex + 1}
                  >
                    <div>
                      <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                    </div>
                  </Tooltip>
                </Group>
              </Input.Wrapper>
              <SegmentedControl
                data={[
                  { label: 'Geral', value: ProposalTipoPrecificacaoEnum.Geral },
                  { label: 'Específica', value: ProposalTipoPrecificacaoEnum.Especifica },
                ]}
                value={form.values.tipoPrecificacao}
                onChange={(value) => {
                  const nextValue = value as ProposalTipoPrecificacaoEnum;
                  form.setFieldValue('tipoPrecificacao', nextValue);
                  onTipoPrecificacaoChangeRef.current?.(nextValue);
                }}
                disabled={props.disabled}
                fullWidth
              />
            </div>
          </Grid.Col>
        )}
        <Grid.Col span={props.withFrequency ? 4 : 3}>
          <BasicValueUnits
            ref={refValueUnits}
            isEdit={props.isEdit}
            disabled={
              props.disabled || !isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
            }
            disableUnit1={props.quantityUnitDisabled}
            disableUnit2={props.frequencyUnitDisabled}
            zIndex={props.zIndex}
            label={`Quantidade${props.withFrequency ? ' / Frequência' : ''}`}
            showUnit2={props.withFrequency}
            validationMode={
              isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive) ? 'all' : 'all-or-none'
            }
            valueMin={props.quantityMin}
            valueDefaultValue={props.quantityDefaultValue}
            valueDecimalScale={props.quantityDecimalScale}
            valueStep={props.quantityStep}
            value={{
              valor: form.values.quantidade,
              idUnidade1: form.values.quantidadeIdUnidadeMedida,
              idUnidade2: form.values.frequenciaIdUnidadeMedida,
            }}
            onChange={(valueUnitsData) => {
              form.setFieldValue('quantidade', valueUnitsData.valor);
              form.setFieldValue('quantidadeIdUnidadeMedida', valueUnitsData.idUnidade1);
              form.setFieldValue('frequenciaIdUnidadeMedida', valueUnitsData.idUnidade2);
              emitChange(valueUnitsData);
            }}
            unit1Options={props.quantityUnitOptions}
            unit2Options={props.frequencyUnitOptions}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          {props.isEdit ? (
            <NumberInput
              leftSection={<IconCurrencyReal size={15} />}
              style={{ width: '100%' }}
              label="Preço"
              placeholder="Preço"
              decimalScale={2}
              step={0.1}
              stepHoldDelay={500}
              stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
              required={isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)}
              hideControls={
                props.disabled || !isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
              }
              disabled={
                props.disabled || !isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
              }
              {...form.getInputProps('preco')}
            />
          ) : (
            <PageViewProperty
              label="Preço"
              text={isNullOrEmpty(form.values.preco) ? '-' : formatCurrency(form.values.preco!)}
              size="sm"
            />
          )}
        </Grid.Col>
        <Grid.Col span={1}>
          {props.isEdit ? (
            <NumberInput
              leftSection={<IconTax size={15} />}
              rightSection="%"
              label="Imposto"
              placeholder="Imposto"
              decimalScale={2}
              hideControls
              // step={0.1}
              // stepHoldDelay={500}
              // stepHoldInterval={(t) => Math.max(1000 / t ** 2, 25)}
              required={isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)}
              disabled={
                props.disabled || !isPricingActive(form.values.tipoPrecificacao, props.forcePricingActive)
              }
              {...form.getInputProps('imposto')}
            />
          ) : (
            <PageViewProperty
              label="Imposto"
              text={form.values.imposto === null ? '-' : `${formatNumber(form.values.imposto)}%`}
              size="sm"
            />
          )}
        </Grid.Col>
      </Grid>
    </Paper>
  );
});

export default BasicPricing;
